// Tool-call rendering — Claude Code style.
//
// Two surfaces:
//   1. ToolCallGroup — single summary line for a batch of consecutive
//      tool calls in one assistant turn. Reads "Ran 3 commands, read 2
//      files" (counts per tool, comma-joined). Rainbow shimmer while
//      anything in the group is running. Click expands to show every
//      individual tool call below.
//   2. ToolCallLine — single call. Status dot + verb + arg preview.
//      Used inside the expanded group and also as a standalone for
//      back-compat (ToolCallCard alias).
//
// Gated by the parent on tweaks.showToolCalls.

import { useState } from "preact/hooks";
import type { ToolCallView } from "../../lib/use-chat";

function statusOf(c: ToolCallView): "running" | "ok" | "denied" | "error" {
  if (c.denied) return "denied";
  if (c.error) return "error";
  if (c.result === null) return "running";
  return "ok";
}

// Map raw tool name + status to a short, human-readable verb phrase
// that always includes some context — "Running bash" not just
// "Running". Falls back to `Running <name>` / `Used <name>` for tools
// we haven't given a dedicated phrase yet.
function verbFor(name: string, status: ReturnType<typeof statusOf>): string {
  const past = status !== "running";
  const M: Record<string, [string, string]> = {
    bash:            ["Running bash",          "Ran bash"],
    host_bash:       ["Running on host",       "Ran on host"],
    read_file:       ["Reading file",          "Read file"],
    write_file:      ["Writing file",          "Wrote file"],
    edit_file:       ["Editing file",          "Edited file"],
    glob:            ["Searching files",       "Searched files"],
    grep:            ["Grepping",              "Grepped"],
    web_search:      ["Searching the web",     "Searched the web"],
    web_fetch:       ["Fetching",              "Fetched"],
    generate_image:  ["Generating image",      "Generated image"],
    todo_write:      ["Updating todos",        "Updated todos"],
    archivist_push:  ["Pushing to Archivist",  "Pushed to Archivist"],
    skill_invoke:    ["Running skill",         "Ran skill"],
    skill_list:      ["Listing skills",        "Listed skills"],
  };
  const entry = M[name];
  if (entry) return past ? entry[1] : entry[0];
  return past ? `Used ${name}` : `Running ${name}`;
}

// Pull the most informative single string from an args object. For
// most tools that's `command` / `query` / `path` / `url` / `prompt`.
// Falls back to a compact JSON one-liner if no known field matches.
function previewOf(args: unknown): string {
  if (args == null) return "";
  if (typeof args === "string") return args.slice(0, 120);
  if (typeof args === "object") {
    const a = args as Record<string, unknown>;
    for (const key of ["command", "query", "path", "url", "prompt", "text", "pattern", "name"]) {
      const v = a[key];
      if (typeof v === "string" && v.trim()) {
        const s = v.trim().replace(/\s+/g, " ");
        return s.length > 120 ? s.slice(0, 120) + "…" : s;
      }
    }
    try {
      const s = JSON.stringify(args);
      return s.length > 100 ? s.slice(0, 100) + "…" : s;
    } catch { return ""; }
  }
  return String(args);
}

function formatJson(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

export function ToolCallLine({ call }: { call: ToolCallView }) {
  const [open, setOpen] = useState(false);
  const status = statusOf(call);
  const verb = verbFor(call.name || "(unknown tool)", status);
  const preview = previewOf(call.args);
  const argsText = formatJson(call.args);
  const resultText = call.result ?? "";

  return (
    <div className="tool-call" data-status={status} data-open={open}>
      <button
        className="tool-call-head"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span className="tool-call-dot" data-status={status} aria-hidden />
        <span className="tool-call-text">
          <span className="tool-call-verb">{verb}</span>
          {preview && (
            <>
              <span className="tool-call-sep" aria-hidden>›</span>
              <span className="tool-call-preview">{preview}</span>
            </>
          )}
        </span>
      </button>

      {open && (
        <div className="tool-call-body">
          {argsText && (
            <>
              <div className="tool-call-section-label">args</div>
              <pre className="tool-call-pre">{argsText}</pre>
            </>
          )}
          {(resultText || call.error) && (
            <>
              <div className="tool-call-section-label">
                {call.error ? "error" : "result"}
              </div>
              <pre className="tool-call-pre">
                {call.error || resultText}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Back-compat alias for older imports that still say ToolCallCard.
export const ToolCallCard = ToolCallLine;

// ─── Group summary ───────────────────────────────────────────────────────

// Friendly phrase for a tool name. Tuple is [past-verb, singular-noun,
// plural-noun]. "$" in the noun means "no object" — render the verb
// alone (e.g. "grepped", "searched the web").
const PHRASES: Record<string, [string, string, string]> = {
  bash:            ["Ran",      "command",   "commands"],
  host_bash:       ["Ran",      "host command", "host commands"],
  read_file:       ["Read",     "file",      "files"],
  write_file:      ["Wrote",    "file",      "files"],
  edit_file:       ["Edited",   "file",      "files"],
  glob:            ["Searched", "file",      "files"],
  grep:            ["Grepped",  "$",         "$"],
  web_search:      ["Searched", "the web",   "the web"],
  web_fetch:       ["Fetched",  "page",      "pages"],
  generate_image:  ["Generated","image",     "images"],
  todo_write:      ["Updated",  "todo list", "todo lists"],
  archivist_push:  ["Pushed to","Archivist", "Archivist"],
  skill_invoke:    ["Ran",      "skill",     "skills"],
  skill_list:      ["Listed",   "skill",     "skills"],
  // Household-app tools — keep the phrase compact and pluralizable.
  calendar_list_events:  ["Checked",  "calendar",   "calendar"],
  calendar_create_event: ["Created",  "event",      "events"],
  calendar_update_event: ["Updated",  "event",      "events"],
  calendar_delete_event: ["Deleted",  "event",      "events"],
  todos_lists:           ["Listed",   "todo list",  "todo lists"],
  todos_list_items:      ["Listed",   "todo",       "todos"],
  todos_add_item:        ["Added",    "todo",       "todos"],
  todos_complete_item:   ["Completed","todo",       "todos"],
  set_alarm:             ["Set",      "alarm",      "alarms"],
  cancel_alarm:          ["Cancelled","alarm",      "alarms"],
  shopping_add_items:    ["Added",    "shopping item", "shopping items"],
  shopping_list:         ["Listed",   "shopping list", "shopping lists"],
  recipe_search:         ["Searched", "recipe",     "recipes"],
  notes_search:          ["Searched", "note",       "notes"],
  notes_read:            ["Read",     "note",       "notes"],
};

function phraseFor(name: string, count: number): string {
  const entry = PHRASES[name];
  if (!entry) {
    return count > 1 ? `Used ${name} (${count})` : `Used ${name}`;
  }
  const [verb, sing, plur] = entry;
  if (sing === "$") {
    // Verb-only (e.g. "grepped"). Lowercase to fit mid-sentence.
    return count > 1 ? `${verb.toLowerCase()} ${count}×` : verb.toLowerCase();
  }
  if (count === 1) return `${verb.toLowerCase()} a ${sing}`;
  return `${verb.toLowerCase()} ${count} ${plur}`;
}

function summarize(calls: ToolCallView[]): string {
  // Preserve order of first occurrence so the summary reads in the
  // order the model actually invoked tools.
  const counts = new Map<string, number>();
  for (const c of calls) {
    const k = c.name || "(unknown)";
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  const parts = Array.from(counts.entries()).map(([name, n]) => phraseFor(name, n));
  if (parts.length === 0) return "";
  // Capitalize just the first segment — rest stay lowercase. Reads
  // naturally: "Ran 3 commands, read 2 files, grepped".
  const first = parts[0];
  parts[0] = first.charAt(0).toUpperCase() + first.slice(1);
  return parts.join(", ");
}

// ─── Reasoning row ───────────────────────────────────────────────────────

// One-line compact row showing the model's chain-of-thought. While
// running: rainbow shimmer + pulsing dot + a live tail of the latest
// reasoning text. Once done: dim "Thought for N chars" label, click to
// expand the full thought. Uses the same dom/CSS scaffold as the tool
// group so they stack visually as siblings.
export function ReasoningRow({ text, running }: { text: string; running: boolean }) {
  const [open, setOpen] = useState(false);
  if (!text && !running) return null;

  // The live tail of the reasoning is intentionally NOT surfaced —
  // the user wants the activity signal ("Thinking" + rainbow shimmer)
  // without the raw thoughts spilling into the chat. The full text is
  // still available behind the click-to-expand chevron.
  const summary = running
    ? "Thinking"
    : `Thought for ${text.length.toLocaleString()} chars`;

  return (
    <div className="tool-call-group" data-status={running ? "running" : "ok"} data-open={open}>
      <button
        className="tool-call-head"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span className="tool-call-dot" data-status={running ? "running" : "ok"} aria-hidden />
        <span className="tool-call-text">
          <span className="tool-call-verb">{summary}</span>
        </span>
        <span className="tool-call-group-chev" aria-hidden>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d={open ? "m18 15-6-6-6 6" : "m9 6 6 6-6 6"} />
          </svg>
        </span>
      </button>
      {open && (
        <div className="tool-call-group-body">
          <pre className="tool-call-pre tool-call-reasoning-pre">{text}</pre>
        </div>
      )}
    </div>
  );
}

export function ToolCallGroup({ calls }: { calls: ToolCallView[] }) {
  const [open, setOpen] = useState(false);
  if (calls.length === 0) return null;

  // Status of the group as a whole: running > error > denied > ok.
  const anyRunning = calls.some((c) => statusOf(c) === "running");
  const anyError = calls.some((c) => statusOf(c) === "error");
  const anyDenied = calls.some((c) => statusOf(c) === "denied");
  const groupStatus: ReturnType<typeof statusOf> = anyRunning ? "running"
    : anyError ? "error"
    : anyDenied ? "denied"
    : "ok";

  const summary = summarize(calls);

  return (
    <div className="tool-call-group" data-status={groupStatus} data-open={open}>
      <button
        className="tool-call-head"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span className="tool-call-dot" data-status={groupStatus} aria-hidden />
        <span className="tool-call-text">
          <span className="tool-call-verb">{summary}</span>
        </span>
        <span className="tool-call-group-chev" aria-hidden>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d={open ? "m18 15-6-6-6 6" : "m9 6 6 6-6 6"} />
          </svg>
        </span>
      </button>
      {open && (
        <div className="tool-call-group-body">
          {calls.map((c) => (
            <ToolCallLine key={c.id} call={c} />
          ))}
        </div>
      )}
    </div>
  );
}
